import React, { useState, useEffect } from 'react'
import { Modal, Form } from 'react-bootstrap'
import { toast } from 'react-toastify'
import CustomButton from '../../Components/CustomButton'
import CustomInput from '../../Components/CustomInput'
import { useUpdateAdminMutation } from '../../Redux/Apis/Admin'
import { useGetNeighborhoodsQuery } from '../../Redux/Apis/Neighborhood'

const EditAdminModal = ({ show, onHide, admin, onSuccess }) => {
  const [updateAdmin, { isLoading }] = useUpdateAdminMutation()
  const { data: neighborhoodsData } = useGetNeighborhoodsQuery()

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'neighborhood_admin',
    assignedNeighborhoods: [],
    defaultNeighborhood: '',
    permissions: {
      deleteUsers: false,
      manageSubscriptions: false
    },
    active: true
  })

  useEffect(() => {
    if (admin) {
      setFormData({
        name: admin.name || '',
        email: admin.email || '',
        role: admin.role || 'neighborhood_admin',
        assignedNeighborhoods: admin.assignedNeighborhoods?.map(n => n._id) || [],
        defaultNeighborhood: admin.defaultNeighborhood?._id || '',
        permissions: {
          deleteUsers: admin.permissions?.deleteUsers || false,
          manageSubscriptions: admin.permissions?.manageSubscriptions || false
        },
        active: admin.active !== undefined ? admin.active : true
      })
    }
  }, [admin, show])

  const handleChange = (field, value) => {
    if (field.startsWith('permissions.')) {
      const permKey = field.split('.')[1]
      setFormData(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [permKey]: value
        }
      }))
    } else {
      setFormData(prev => ({ ...prev, [field]: value }))
    }
  }

  const handleRoleChange = (role) => {
    setFormData(prev => ({
      ...prev,
      role,
      permissions: role === 'super_admin' ? {
        manageNeighborhoods: true,
        manageAdmins: true,
        accessAllNeighborhoods: true,
        deleteUsers: true,
        manageSubscriptions: true
      } : {
        manageNeighborhoods: false,
        manageAdmins: false,
        accessAllNeighborhoods: false,
        deleteUsers: prev.permissions.deleteUsers,
        manageSubscriptions: prev.permissions.manageSubscriptions
      }
    }))
  }

  const handleNeighborhoodToggle = (neighborhoodId) => {
    setFormData(prev => {
      const assigned = prev.assignedNeighborhoods.includes(neighborhoodId)
        ? prev.assignedNeighborhoods.filter(id => id !== neighborhoodId)
        : [...prev.assignedNeighborhoods, neighborhoodId]

      return { ...prev, assignedNeighborhoods: assigned }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (formData.role === 'neighborhood_admin' && formData.assignedNeighborhoods.length === 0) {
      toast.error('Neighborhood admin must have at least one assigned neighborhood')
      return
    }

    try {
      // Clean up the data before sending
      const payload = { ...formData }

      // Convert empty string to null for defaultNeighborhood
      if (payload.defaultNeighborhood === '' || !payload.defaultNeighborhood) {
        payload.defaultNeighborhood = null
      }

      // If super admin, clear assigned neighborhoods (they have access to all)
      if (payload.role === 'super_admin') {
        payload.assignedNeighborhoods = []
        payload.defaultNeighborhood = null
      }

      await updateAdmin({
        id: admin._id,
        payload
      }).unwrap()

      toast.success('Admin updated successfully')
      onSuccess()
      onHide()
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to update admin')
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="edit-admin-modal">
      <Modal.Header closeButton className="modal-header-custom">
        <Modal.Title className="mainTitle">Edit Admin</Modal.Title>
      </Modal.Header>

      <Modal.Body className="modal-body-custom">
        <div className="admin-info-banner mb-4">
          <div className="d-flex align-items-center">
            {admin?.picture && (
              <img src={admin.picture} alt={admin?.name} className="admin-avatar-large me-3" />
            )}
            <div>
              <h5 className="mb-1 fw-bold">{admin?.name}</h5>
              <p className="mb-0 text-muted">{admin?.email}</p>
            </div>
          </div>
        </div>

        <Form onSubmit={handleSubmit}>
          <div className="row">
            {/* Basic Information */}
            <div className="col-12 mb-3">
              <div className="section-divider">
                <h5 className="section-title">Basic Information</h5>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="Name"
                type="text"
                placeholder="Admin Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />
            </div>

            <div className="col-md-6 mb-3">
              <CustomInput
                label="Email"
                type="email"
                placeholder="admin@theturn.com"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                disabled
              />
              <small className="text-muted">Email cannot be changed</small>
            </div>

            {/* Role Selection */}
            <div className="col-12 mb-3 mt-3">
              <div className="section-divider">
                <h5 className="section-title">Role & Access Level</h5>
              </div>
            </div>

            <div className="col-md-12 mb-3">
              <div className="role-selection-container">
                <div className={`role-option ${formData.role === 'super_admin' ? 'selected' : ''}`}>
                  <Form.Check
                    type="radio"
                    id="role-super"
                    label=""
                    name="role"
                    checked={formData.role === 'super_admin'}
                    onChange={() => handleRoleChange('super_admin')}
                  />
                  <label htmlFor="role-super" className="role-label">
                    <div className="fw-bold">Super Admin</div>
                    <small className="text-muted">Full access to all neighborhoods and features</small>
                  </label>
                </div>
                <div className={`role-option ${formData.role === 'neighborhood_admin' ? 'selected' : ''}`}>
                  <Form.Check
                    type="radio"
                    id="role-neighborhood"
                    label=""
                    name="role"
                    checked={formData.role === 'neighborhood_admin'}
                    onChange={() => handleRoleChange('neighborhood_admin')}
                  />
                  <label htmlFor="role-neighborhood" className="role-label">
                    <div className="fw-bold">Neighborhood Admin</div>
                    <small className="text-muted">Limited to assigned neighborhoods only</small>
                  </label>
                </div>
              </div>
            </div>

            {/* Neighborhood Assignment */}
            {formData.role === 'neighborhood_admin' && (
              <>
                <div className="col-12 mb-3 mt-3">
                  <div className="section-divider">
                    <h5 className="section-title">Assigned Neighborhoods</h5>
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="neighborhood-checkboxes">
                    {(neighborhoodsData?.data || []).map(neighborhood => (
                      <div key={neighborhood._id} className="neighborhood-checkbox-item">
                        <Form.Check
                          type="checkbox"
                          id={`neighborhood-${neighborhood._id}`}
                          label={neighborhood.name}
                          checked={formData.assignedNeighborhoods.includes(neighborhood._id)}
                          onChange={() => handleNeighborhoodToggle(neighborhood._id)}
                        />
                        <small className="text-muted ms-4">{neighborhood.slug}</small>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Default Neighborhood */}
                {formData.assignedNeighborhoods.length > 0 && (
                  <div className="col-md-12 mb-3">
                    <label className="mainLabel">Default Neighborhood</label>
                    <Form.Select
                      value={formData.defaultNeighborhood}
                      onChange={(e) => handleChange('defaultNeighborhood', e.target.value)}
                    >
                      <option value="">Select default...</option>
                      {(neighborhoodsData?.data || [])
                        .filter(n => formData.assignedNeighborhoods.includes(n._id))
                        .map(neighborhood => (
                          <option key={neighborhood._id} value={neighborhood._id}>
                            {neighborhood.name}
                          </option>
                        ))}
                    </Form.Select>
                  </div>
                )}
              </>
            )}

            {/* Permissions */}
            {formData.role === 'neighborhood_admin' && (
              <>
                <div className="col-12 mb-3 mt-3">
                  <div className="section-divider">
                    <h5 className="section-title">Additional Permissions</h5>
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="permissions-container">
                    <div className="permission-item">
                      <Form.Check
                        type="checkbox"
                        id="perm-delete"
                        label="Delete Users"
                        checked={formData.permissions.deleteUsers}
                        onChange={(e) => handleChange('permissions.deleteUsers', e.target.checked)}
                      />
                      <small className="text-muted d-block ms-4">Can permanently delete user accounts</small>
                    </div>
                    <div className="permission-item">
                      <Form.Check
                        type="checkbox"
                        id="perm-subscriptions"
                        label="Manage Subscriptions"
                        checked={formData.permissions.manageSubscriptions}
                        onChange={(e) => handleChange('permissions.manageSubscriptions', e.target.checked)}
                      />
                      <small className="text-muted d-block ms-4">Can view and manage subscription plans</small>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Status */}
            <div className="col-12 mb-3 mt-3">
              <div className="section-divider">
                <h5 className="section-title">Account Status</h5>
              </div>
            </div>

            <div className="col-md-12 mb-3">
              <div className={`status-toggle-container ${formData.active ? 'status-active' : 'status-inactive'}`}>
                <Form.Check
                  type="switch"
                  id="admin-status-switch"
                  label={formData.active ? 'Account Active' : 'Account Inactive'}
                  checked={formData.active}
                  onChange={(e) => handleChange('active', e.target.checked)}
                  className="status-switch"
                />
                <small className="text-muted d-block mt-2">
                  {formData.active ? '✓ Admin can login and access the panel' : '✗ Admin cannot login'}
                </small>
              </div>
            </div>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer className="modal-footer-custom">
        <CustomButton
          variant="secondaryButton"
          text="Cancel"
          onClick={onHide}
          disabled={isLoading}
        />
        <CustomButton
          variant="primaryButton"
          text={isLoading ? 'Saving...' : 'Save Changes'}
          onClick={handleSubmit}
          disabled={isLoading}
        />
      </Modal.Footer>
    </Modal>
  )
}

export default EditAdminModal

